/**
 * Lane planner API client for the vendor-neutral endpoints
 * (`/shipment/v1/lane-planner/...`). One search fans out to every vendor the
 * company has connected and returns `NormalizedQuote`s, whichever vendor
 * answered. Types mirror `shipment/serializer/lane_planner_serializers.py`
 * (request) and `shipment/lane_planner/domain.py` (quotes); keep them in sync.
 */
import { AxiosResponse } from 'axios';

import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';

export type LaneMode = 'air' | 'ocean' | 'road';

// ---------------------------------------------------------------------------
// Search request
// ---------------------------------------------------------------------------

/** A location with a code per kind a vendor may need (airport, port, ...). */
export interface LanePlace {
  label?: string | null;
  codes: Partial<Record<'airport' | 'port' | 'city' | 'address', string>>;
}

export interface OceanItemRequest {
  quantity: number;
  weight_kg: number;
  container_size_type?: string | null;
  unit_type?: 'PALLET' | 'BOX' | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  volume_m3?: number | null;
  stackable?: boolean | null;
  turnable?: boolean | null;
}

export interface OceanCargoRequest {
  shipment_type: 'FCL' | 'LCL';
  items: OceanItemRequest[];
  commodity?: string | null;
  hazmat?: boolean;
  totals_only?: boolean;
  include_origin_charges?: boolean;
  include_destination_charges?: boolean;
}

export interface AirPieceRequest {
  pieces: number;
  length_cm: number;
  width_cm: number;
  height_cm: number;
  gross_weight_kg: number;
  weight_per_item?: boolean;
  stackable?: boolean;
  turnable?: boolean;
}

export interface AirUldRequest {
  units: number;
  uld_type: string;
  gross_weight_kg: number;
  volume_m3: number;
}

export interface AirCargoRequest {
  service_level: 'general' | 'passive_temp' | 'active_container';
  shipment_type: 'loose' | 'uld';
  temp_range?: string | null;
  goods_category?:
    'pharmaceuticals' | 'perishables' | 'special_cargo' | 'other' | null;
  commodity?: string | null;
  screened?: boolean;
  consolidated?: boolean;
  known_shipper?: boolean | null;
  lithium_battery?: string | null;
  pieces?: AirPieceRequest[];
  ulds?: AirUldRequest[];
  container_model?: string | null;
  container_quantity?: number | null;
  container_weight_kg?: number | null;
}

/** Road is typed by `type`; each type accepts only its own fields. */
export type RoadCargoRequest =
  | {
      type: 'ltl';
      pallets: number;
      weight_per_pallet_kg: number;
      equipment?: string | null;
      freight_class?: string | null;
      nmfc_code?: string | null;
      accessorials?: string[];
      length_cm?: number | null;
      width_cm?: number | null;
      height_cm?: number | null;
    }
  | {
      type: 'parcel';
      packages: number;
      weight_per_package_kg: number;
      parcel_service?: string | null;
      residential?: boolean;
      length_cm?: number | null;
      width_cm?: number | null;
      height_cm?: number | null;
    }
  | {
      type: 'ftl';
      equipment: string;
      temp_range?: string | null;
      team_drivers?: boolean;
      dropped_trailer?: boolean;
    };

/** The LTL variant of the road block: what pickup and delivery trucking is priced as. */
export type LtlCargoRequest = Extract<RoadCargoRequest, { type: 'ltl' }>;

/**
 * Trucking at the ends of an air or ocean search: LTL from the origin address
 * to the origin airport or port (`pickup`) and from the destination airport or
 * port to the destination address (`delivery`). A road mode beside it is quoted as usual.
 */
export interface DoorToDoorRequest {
  pickup: boolean;
  delivery: boolean;
  road: LtlCargoRequest;
}

/** Candidate airports (IATA) and ports (UN/LOCODE) for one end of a lane. */
export interface LaneSites {
  airport?: string[];
  port?: string[];
}

/** An airport or port near a place, from `GET places/resolve/`. */
export interface LaneSite {
  code: string;
  name: string;
  city: string;
  country: string;
  distance_km: number;
  /** What a search uses unless the user changes it: the nearest few within 150 km. */
  default: boolean;
}

export interface ResolvedSites {
  point: { lat: number; lng: number };
  airports: LaneSite[];
  ports: LaneSite[];
}

/** Body of `POST searches/`. Cargo blocks are required for exactly the selected modes. */
export interface LaneSearchRequest {
  origin: LanePlace;
  destination: LanePlace;
  /** Every airport and port pair is searched as its own lane; none = the codes on the places. */
  origin_sites?: LaneSites;
  destination_sites?: LaneSites;
  /** YYYY-MM-DD */
  ship_date: string;
  flexibility_days?: number;
  modes: LaneMode[];
  ocean?: OceanCargoRequest;
  air?: AirCargoRequest;
  road?: RoadCargoRequest;
  /** Pickup and delivery trucking for air and ocean; see `DoorToDoorRequest`. */
  door_to_door?: DoorToDoorRequest;
}

// ---------------------------------------------------------------------------
// Quotes and searches
// ---------------------------------------------------------------------------

export interface LaneLeg {
  mode: LaneMode;
  carrier: string | null;
  carrier_code: string | null;
  logo_url: string | null;
  origin: string | null;
  destination: string | null;
  departs_at: string | null;
  arrives_at: string | null;
  service_ref: string | null;
}

export type ChargeKind = 'base' | 'fuel_surcharge' | 'service' | 'other';

export interface LaneCharge {
  kind: ChargeKind;
  label: string;
  /** Exact decimal string. */
  amount: string;
}

export type TruckingLeg = 'pickup' | 'delivery';

/** One trucking leg of an air or ocean quote: the airport or port and the cheapest LTL price. */
export interface QuoteTruckingLeg {
  site: string;
  /** Exact decimal string. */
  price: string;
}

/**
 * The trucking at an air or ocean quote's own origin and destination sites.
 * `total` is the quote plus every leg that applies, null until all are priced
 * in the quote's currency; `excludes` are legs that cannot apply to this site
 * (the search's `access_legs` say why).
 */
export interface QuoteDoorToDoor {
  pickup: QuoteTruckingLeg | null;
  delivery: QuoteTruckingLeg | null;
  total: string | null;
  currency: string | null;
  status: 'pending' | 'complete' | 'incomplete';
  excludes: TruckingLeg[];
}

/** What every vendor returns. Missing vendor data is `null`, never zero. */
export interface NormalizedQuote {
  /** Zoomlogi id of the stored quote. */
  id: string;
  vendor: string;
  vendor_quote_id: string;
  mode: LaneMode;
  /** The airport or port pair this quote is for; absent from older backends and for road. */
  route?: LaneRoute | null;
  carrier: string | null;
  /** Exact decimal string, never a float. */
  price: string | null;
  currency: string | null;
  /** `offer` is a bookable vendor offer, `tariff` an indicative price-list rate. */
  rate_type: 'offer' | 'tariff';
  bookable: boolean;
  legs: LaneLeg[];
  charges: LaneCharge[];
  valid_until: string | null;
  transit_minutes: number | null;
  temperature_range: string | null;
  monitoring: string | null;
  co2_kg: number | null;
  capacity: string | null;
  cold_chain: string | null;
  details: Record<string, unknown>;
  /** Only on air and ocean quotes of a search that asked for trucking. */
  door_to_door?: QuoteDoorToDoor;
}

// ---------------------------------------------------------------------------
// Booking. Mirrors `CreateBookingSerializer` and `booking_to_dict` in the backend.
// ---------------------------------------------------------------------------

export interface BookingParty {
  company: string;
  contact?: string;
  address: string;
  facility_hours?: string;
}

export interface BookingCommodity {
  description: string;
  po_reference?: string;
  pickup_number?: string;
  handling_notes?: string;
}

/** Choices only some vendors ask for: an air waybill, from the booking form. */
export interface BookingOptions {
  waybill_type?: 'PAPER' | 'EAW' | 'EAP';
  waybill_prefix?: string;
  waybill_number?: string;
}

export interface CreateLaneBookingRequest {
  quote_id: string;
  shipper: BookingParty;
  consignee: BookingParty;
  commodity: BookingCommodity;
  options?: BookingOptions;
}

export type LaneBookingStatus = 'pending' | 'created' | 'failed';

export interface LaneBooking {
  id: string;
  /** Our own reference, `ZL-YYMMDD-NNNN`. */
  reference: string;
  status: LaneBookingStatus;
  error_message: string | null;
  vendor: string;
  mode: LaneMode;
  /** The vendor's lifecycle word, e.g. QUEUED then CONFIRMED. */
  vendor_status: string | null;
  awb_number: string | null;
  shipper: BookingParty;
  consignee: BookingParty;
  commodity: BookingCommodity;
  quote: {
    id: string;
    carrier: string | null;
    price: string | null;
    currency: string | null;
    origin: string | null;
    destination: string | null;
    departs_at: string | null;
    transit_minutes: number | null;
  };
  created_at: string;
}

export type VendorSearchStatus = 'pending' | 'done' | 'failed' | 'unsupported';

/**
 * Why a finished vendor search ended as it did (null while pending, or done
 * with quotes). `credentials` and `not_connected` need an admin; `unsupported`
 * is expected (the vendor does not quote this mode or request); `refused` is a
 * "no" for this lane (for example an airport the vendor does not serve);
 * `no_rates` is an answer with nothing to offer; `timeout`, `unavailable` and
 * `error` are worth retrying.
 */
/** The airport or port pair a quote or vendor search is for, with each end's IANA zone (absent from older backends). */
export interface LaneRoute {
  origin: string;
  destination: string;
  origin_timezone?: string | null;
  destination_timezone?: string | null;
}

export type VendorOutcomeKind =
  | 'credentials'
  | 'not_connected'
  | 'unsupported'
  | 'refused'
  | 'no_rates'
  | 'timeout'
  | 'unavailable'
  | 'error';

export interface VendorSearch {
  id: string;
  vendor: string;
  display_name: string;
  mode: LaneMode;
  status: VendorSearchStatus;
  /** Failure reason, or why the vendor was skipped. */
  message: string | null;
  warnings: string[];
  /** The airport or port pair this row searched; null for road and for a skipped mode. */
  route?: LaneRoute | null;
  /** Absent from backends older than the explain-the-result change. */
  kind?: VendorOutcomeKind | null;
  retryable?: boolean;
}

/** Why a trucking leg has no price: a fixed set, so the wording is ours. */
export type TruckingReasonKind =
  | 'outside_us'
  | 'no_address'
  | 'city_only'
  | 'bad_address'
  | 'not_searched'
  | VendorOutcomeKind;

/** One priced LTL quote for a trucking leg. */
export interface TruckingQuote {
  carrier: string | null;
  service_level: string | null;
  /** Exact decimal string. */
  price: string;
  currency: string | null;
  transit_minutes: number | null;
}

/** A trucking quote to pick between; `id` is the stored quote's id. */
export interface TruckingOption extends TruckingQuote {
  id: string;
}

/** The trucking between an address and one airport or port, from the search's `access_legs`. */
export interface AccessLeg {
  status: 'pending' | 'priced' | 'unpriced' | 'unavailable';
  kind?: TruckingReasonKind;
  /** The address end was priced at its city's centre ZIP (only from a backend before `city_only`). */
  city_centre?: boolean;
  /** Display text of the two ends, e.g. `2390 Mission St` and `SJC`. */
  from: string;
  to: string;
  best?: TruckingQuote;
  /** Every priced quote of the leg, cheapest first (`best` is the first); absent from older backends. */
  options?: TruckingOption[];
}

export type TruckingCaveat =
  'hazmat' | 'temperature_controlled' | 'uld' | 'fcl';

/** Present only on a search that asked for trucking. */
export interface AccessLegs {
  caveats: TruckingCaveat[];
  pickup: Record<string, AccessLeg>;
  delivery: Record<string, AccessLeg>;
}

export interface LaneSearch {
  id: string;
  modes: LaneMode[];
  origin: string;
  destination: string;
  ship_date: string;
  /** The form inputs the search ran with; absent from older backends. */
  params?: LaneSearchRequest;
  created_at: string;
  /** True once every vendor search ended. */
  done: boolean;
  /** How long to wait before polling again (null once done); absent from older backends. */
  poll_after_ms?: number | null;
  vendors: VendorSearch[];
  quotes: NormalizedQuote[];
  /** Trucking per airport or port; only for a search that asked for it. */
  access_legs?: AccessLegs;
  /** Lowest price per departure day: `{mode: {currency: {date: price}}}`. */
  day_prices: Record<string, Record<string, Record<string, string>>>;
}

export interface LaneConfig {
  enabled: boolean;
  modes: LaneMode[];
  /** A configured vendor quotes road, so air and ocean searches can add trucking; absent from older backends. */
  door_to_door?: boolean;
  /** The company may see CO₂ estimates on quotes; absent from older backends. */
  emissions?: boolean;
  vendors: Array<{ code: string; display_name: string; modes: LaneMode[] }>;
}

export interface LanePlaceResult {
  label: string | null;
  codes: LanePlace['codes'];
  vendor: string;
}

export interface SavedLaneSearch {
  id: string;
  modes: LaneMode[];
  origin: string;
  destination: string;
  ship_date: string;
  saved_name: string | null;
  saved_at: string | null;
  /** The stored `LaneSearchRequest`, to run again. */
  params: LaneSearchRequest;
  created_at: string;
}

export interface SavedLaneQuote {
  id: string;
  ref_number: string | null;
  quote: NormalizedQuote;
  search_params: LaneSearchRequest;
  origin: string;
  destination: string;
  ship_date: string;
  created_at: string;
}

export interface LanePage<T> {
  items: T[];
  next_cursor: string | null;
  has_more: boolean;
}

// ---------------------------------------------------------------------------
// Envelope handling
// ---------------------------------------------------------------------------

/** The payload of a successful call (responses are wrapped twice). */
export function unwrapLanePayload<T>(res: AxiosResponse<unknown>): T {
  const body = res?.data as { data?: { data?: unknown } } | undefined;
  const payload = body?.data?.data;
  if (payload === undefined || payload === null) throw res;
  return payload as T;
}

const formatFieldErrors = (errors: unknown, prefix = ''): string | null => {
  if (!errors || typeof errors !== 'object') return null;
  if (Array.isArray(errors)) {
    const text = errors.filter((v) => typeof v === 'string').join(' ');
    return text || null;
  }
  const parts: string[] = [];
  for (const [field, value] of Object.entries(
    errors as Record<string, unknown>
  )) {
    const label =
      field === 'non_field_errors'
        ? prefix
        : prefix
          ? `${prefix}.${field}`
          : field;
    if (typeof value === 'string') {
      parts.push(label ? `${label}: ${value}` : value);
    } else if (Array.isArray(value) && value.length) {
      const text = value.filter((v) => typeof v === 'string').join(' ');
      if (text) parts.push(label ? `${label}: ${text}` : text);
    } else if (value && typeof value === 'object') {
      const nested = formatFieldErrors(value, label);
      if (nested) parts.push(nested);
    }
  }
  return parts.length ? parts.join(' ') : null;
};

/** The server's reason for a failed call: field errors first, then its message. */
export function laneErrorMessage(err: unknown, fallback: string): string {
  const res = err as AxiosResponse<unknown> | undefined;
  const body = res?.data as Record<string, unknown> | undefined;
  const envelope = body?.data as Record<string, unknown> | undefined;
  const nestedData = envelope?.data as Record<string, unknown> | undefined;
  const fieldErrors =
    formatFieldErrors(nestedData?.error) ||
    formatFieldErrors(envelope?.error) ||
    formatFieldErrors(body?.error);
  if (fieldErrors) return fieldErrors;
  if (typeof envelope?.message === 'string' && envelope.message) {
    return envelope.message;
  }
  if (typeof body?.message === 'string' && body.message) return body.message;
  return fallback;
}

/** HTTP status of a failed call, whether axios or the interceptor rejected it. */
export const laneErrorStatus = (err: unknown): number | undefined =>
  (err as { status?: number } | undefined)?.status ??
  (err as { response?: { status?: number } } | undefined)?.response?.status;

// ---------------------------------------------------------------------------
// Calls. Failures reject with the response, so callers show `laneErrorMessage`.
// ---------------------------------------------------------------------------

const rejectWithResponse =
  (reject: (reason: unknown) => void) =>
  (error: { response?: unknown }): void =>
    reject(error?.response ?? error);

const searchPath = (searchId: string): string =>
  `${APIPaths.laneSearches}${encodeURIComponent(searchId)}/`;

const pageQuery = (limit?: number, cursor?: string): string => {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const GetLaneConfig = (token: string): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.laneConfig, token)
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const SearchLanePlaces = (
  token: string,
  query: string,
  mode: LaneMode
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.lanePlaces}?query=${encodeURIComponent(query)}&mode=${mode}`,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Airports and ports near a point, or around the site with this IATA/UN-LOCODE code. */
export const ResolveLaneSites = (
  token: string,
  where: { lat: number; lng: number } | { code: string }
): Promise<AxiosResponse<unknown>> => {
  const query =
    'code' in where
      ? `code=${encodeURIComponent(where.code)}`
      : `lat=${where.lat}&lng=${where.lng}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.lanePlacesResolve}?${query}`, token)
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });
};

/** An airport or port whose code, name or city matches typed text, from `GET places/sites/`. */
export interface LaneSiteMatch {
  kind: 'airport' | 'port';
  code: string;
  name: string;
  city: string;
  country: string;
}

/** Every bundled airport and port matching the text, best match first. */
export const SearchLaneSites = (
  token: string,
  query: string,
  kind?: LaneSiteMatch['kind']
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.lanePlacesSites}?query=${encodeURIComponent(query)}${
          kind ? `&kind=${kind}` : ''
        }`,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const CreateLaneSearch = (
  token: string,
  request: LaneSearchRequest
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.laneSearches, request, token)
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Reads a search; the server polls any vendor still working. Polls leave
 * `includeParams` off: the form inputs are needed once, when opening it. */
export const GetLaneSearch = (
  token: string,
  searchId: string,
  signal?: AbortSignal,
  options?: { includeParams?: boolean }
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${searchPath(searchId)}${options?.includeParams ? '?include=params' : ''}`,
        token,
        undefined,
        signal
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Searches more origin x destination airports or ports on a search; answers with the whole search. */
export const AddLaneSearchLanes = (
  token: string,
  searchId: string,
  sites: { origin_sites: LaneSites; destination_sites: LaneSites }
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${searchPath(searchId)}lanes/`, sites, token)
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const RetryVendorSearch = (
  token: string,
  searchId: string,
  vendorSearchId: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${searchPath(searchId)}vendor-searches/${encodeURIComponent(vendorSearchId)}/retries/`,
        undefined,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const GetSavedLaneSearches = (
  token: string,
  limit?: number,
  cursor?: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.laneSavedSearches}${pageQuery(limit, cursor)}`,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Pins a search to the Saved tab (re-pinning renames it). */
export const SaveLaneSearch = (
  token: string,
  searchId: string,
  name: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Put',
        `${APIPaths.laneSavedSearches}${encodeURIComponent(searchId)}/`,
        { name },
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const DeleteSavedLaneSearch = (
  token: string,
  searchId: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosDelete(
        'Delete',
        `${APIPaths.laneSavedSearches}${encodeURIComponent(searchId)}/`,
        null,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const GetSavedLaneQuotes = (
  token: string,
  limit?: number,
  cursor?: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.laneSavedQuotes}${pageQuery(limit, cursor)}`,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Saves a quote from a search's results. */
export const SaveLaneQuote = (
  token: string,
  quoteId: string,
  refNumber?: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Put',
        `${APIPaths.laneSavedQuotes}${encodeURIComponent(quoteId)}/`,
        refNumber ? { ref_number: refNumber } : {},
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

export const DeleteSavedLaneQuote = (
  token: string,
  quoteId: string
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosDelete(
        'Delete',
        `${APIPaths.laneSavedQuotes}${encodeURIComponent(quoteId)}/`,
        null,
        token
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Books a quote. A vendor refusal in its answer still resolves, with `status: 'failed'`. */
export const CreateLaneBooking = (
  token: string,
  body: CreateLaneBookingRequest
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.laneBookings, body, token)
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });

/** Polls a booking; the server asks the vendor again while it is still confirming. */
export const GetLaneBooking = (
  token: string,
  bookingId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<unknown>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.laneBookings}${encodeURIComponent(bookingId)}/`,
        token,
        undefined,
        signal,
        true
      )
      .then(resolve)
      .catch(rejectWithResponse(reject));
  });
